import { createContext, useContext, ReactNode, useState } from 'react';
import { useSession } from '@/lib/auth-client';

/**
 * UserContext - Tier Management for Platform-UI
 *
 * Provides user tier information across the application.
 * Integrates with Better Auth session data.
 *
 * Tier Hierarchy:
 * - free: Basic variant generation (limited parameters)
 * - basic: More parameters + batch generation
 * - pro: Advanced parameters + priority queue
 * - designer: Full parameter access + API access
 *
 * TODO: After Better Auth API deployed, read tier from session.user.tier
 * For now, uses development tier switcher in FDAIA page
 */

export type UserTier = 'free' | 'basic' | 'pro' | 'designer';

interface UserContextValue {
  userId: string | null;
  userEmail: string | null;
  userTier: UserTier;
  userRole: string | null;
  isAuthenticated: boolean;
  setUserTier: (tier: UserTier) => void; // Dev only - remove after auth deployed
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const { data: session } = useSession();
  const [devTier, setDevTier] = useState<UserTier>('free');

  // Extract user data from session
  const userId = session?.id || null;
  const userEmail = session?.email || null;
  const userRole = session?.role || null;

  // TODO: After Better Auth API deployed, use session.tier instead of devTier
  const userTier = (session as any)?.tier || devTier;
  const isAuthenticated = !!session;

  const value: UserContextValue = {
    userId,
    userEmail,
    userTier,
    userRole,
    isAuthenticated,
    setUserTier: setDevTier, // Dev only
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/**
 * Hook to access user context
 */
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

/**
 * Tier-based feature gating utilities
 */
export const TIER_FEATURES = {
  free: {
    maxParameters: 3,
    batchGeneration: false,
    advancedParameters: false,
    priorityQueue: false,
    apiAccess: false,
    variantsPerDay: 5,
  },
  basic: {
    maxParameters: 6,
    batchGeneration: true,
    advancedParameters: false,
    priorityQueue: false,
    apiAccess: false,
    variantsPerDay: 25,
  },
  pro: {
    maxParameters: 10,
    batchGeneration: true,
    advancedParameters: true,
    priorityQueue: true,
    apiAccess: false,
    variantsPerDay: 100,
  },
  designer: {
    maxParameters: Infinity,
    batchGeneration: true,
    advancedParameters: true,
    priorityQueue: true,
    apiAccess: true,
    variantsPerDay: Infinity,
  },
} as const;

/**
 * Check if user has access to a feature
 */
export function hasFeature(tier: UserTier, feature: keyof typeof TIER_FEATURES.free): boolean {
  return TIER_FEATURES[tier][feature] !== false;
}

/**
 * Get max parameter count for tier
 */
export function getMaxParameters(tier: UserTier): number {
  return TIER_FEATURES[tier].maxParameters;
}
